"use client";

import Link from "next/link";
import {
  useParams as useNextParams,
  usePathname as useNextPathname,
  useRouter as useNextRouter,
} from "next/navigation";
import type { UrlObject } from "node:url";
import React, { type ComponentProps, type Ref } from "react";

type LinkProps = ComponentProps<typeof Link>;
type AppRouterInstance = ReturnType<typeof useNextRouter>;
type NavigateOptions = Parameters<AppRouterInstance["push"]>[1];

const useLocale = (): string => {
  const params = useNextParams();
  const locale = params?.locale;
  return typeof locale === "string" ? locale : "";
};

export const usePathname = () => {
  const pathname = useNextPathname();
  const locale = useLocale();

  if (!pathname) {
    return pathname;
  }

  // If we are on a localized path, strip the locale
  if (locale && pathname.startsWith(`/${locale}`)) {
    const newPathname = pathname.replace(`/${locale}`, "");
    return newPathname === "" ? "/" : newPathname;
  }

  return pathname;
};

const prefixLocale = (path: string, locale: string): string =>
  path.startsWith("/") && locale && !path.startsWith(`/${locale}`) ? `/${locale}${path}` : path;

export const useRouter = () => {
  const router = useNextRouter();
  const locale = useLocale();

  return {
    ...router,
    push: (href: string, options?: NavigateOptions) =>
      router.push(prefixLocale(href, locale), options),
    replace: (href: string, options?: NavigateOptions) =>
      router.replace(prefixLocale(href, locale), options),
  };
};

// `href` is a plain string or UrlObject (not next's typed `Route`): this Link
// localizes arbitrary internal paths and is also passed to libraries
// (better-auth-ui) whose Link contract is `{ href: string }`.
type LocalizedLinkProps = Omit<LinkProps, "href"> & {
  readonly href: string | UrlObject;
  readonly ref?: Ref<HTMLAnchorElement>;
};

const localizeHref = (href: string | UrlObject, locale: string): string | UrlObject => {
  if (typeof href === "string") {
    return prefixLocale(href, locale);
  }
  if (typeof href.pathname === "string") {
    return { ...href, pathname: prefixLocale(href.pathname, locale) };
  }
  return href;
};

function LocalizedLink(props: LocalizedLinkProps) {
  const { href, ref, ...rest } = props;
  const locale = useLocale();

  const localizedHref = localizeHref(href, locale);

  return React.createElement(
    Link,
    {
      ...rest,
      ref,
      href: localizedHref,
    },
    props.children
  );
}

LocalizedLink.displayName = "LocalizedLink";

export { LocalizedLink as Link };
